import { Injectable, Type } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractService } from './abstract-service';
import { SolText } from '../models/core/sol-text';
import { SolDialogType } from '../models/core/sol-dialog';
import { SolConfirmDialogComponent } from '../components/dialog/sol-confirm-dialog/sol-confirm-dialog.component';
import { SolInfoDialogComponent } from '../components/dialog/sol-info-dialog/sol-info-dialog.component';

// ─── Entry type ───

export interface SolComponentEntry {
  id: string;
  type: 'component';
  mode: 'dialog' | 'wizard';
  componentType: Type<any>;
  configurer: (instance: any) => void;
}

// ─── Ref ───

export class DialogRef<T> {
  constructor(private readonly instanceGetter: () => T) {}

  public get instance(): T {
    return this.instanceGetter();
  }
}

// ─── State ───

export interface DialogServiceState {
  entries: SolComponentEntry[];
}

// ─── Service ───

@Injectable({ providedIn: 'root' })
export class DialogService extends AbstractService<DialogServiceState> {
  private counter = 1;
  private instances: Record<string, any> = {};

  constructor() {
    super({ entries: [] });
  }

  // ─── Component dialogs/wizards ───

  public showDialog<T>(componentType: Type<T>, configurer: (instance: T) => void = () => {}): DialogRef<T> {
    return this.showComponent<T>('dialog', componentType, configurer);
  }

  public showWizard<T>(componentType: Type<T>, configurer: (instance: T) => void = () => {}): DialogRef<T> {
    return this.showComponent<T>('wizard', componentType, configurer);
  }

  public showConfirm(
    title: SolText,
    text: SolText,
    options: { confirmText?: SolText; type?: SolDialogType; onConfirm?: () => void | Observable<void> } = {},
  ): DialogRef<SolConfirmDialogComponent> {
    return this.showDialog(SolConfirmDialogComponent, instance => {
      instance.configure(title, text, options.confirmText, options.type);
      if (options.onConfirm) {
        instance.onConfirm = options.onConfirm;
      }
    });
  }

  public showInfo(
    title: SolText,
    text: SolText,
    options: { type?: SolDialogType } = {},
  ): DialogRef<SolInfoDialogComponent> {
    return this.showDialog(SolInfoDialogComponent, instance => {
      instance.configure(title, text, options.type);
    });
  }

  // ─── Internal ───

  public closeEntry(entryId: string): void {
    this.update(s => ({ entries: s.entries.filter(e => e.id !== entryId) }));
    delete this.instances[entryId];
  }

  public register(entryId: string, instance: any): void {
    this.instances[entryId] = instance;
  }

  private showComponent<T>(
    mode: 'dialog' | 'wizard',
    componentType: Type<T>,
    configurer: (instance: T) => void,
  ): DialogRef<T> {
    const id = 'dialog' + this.counter++;
    const entry: SolComponentEntry = { id, type: 'component', mode, componentType, configurer };
    this.addEntry(entry);
    return new DialogRef<T>(() => this.instances[id]);
  }

  private addEntry(entry: SolComponentEntry): void {
    this.update(s => ({ entries: [...s.entries, entry] }));
  }
}
